import React from "react";
import clsx from "clsx";
import AdminIcon from "../../../../assets/icons/admin-icon.svg";
import InviteeIcon from "../../../../assets/icons/invitee-icon.svg";
import { getVenueIcon } from "../../../../utils/utility";

const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 5v3l1.8 1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PersonIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="5.5" r="2.8" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2.5 14c0-3.04 2.46-5.5 5.5-5.5s5.5 2.46 5.5 5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ScheduledEventCard = ({
  event,
  showCheckbox = false,
  selected = false,
  onToggle,
  onViewDetail,
  subtitle,
  showRole = true,
  extra,
  actions,
  variant,
}) => {
  const cancelled = event.status === "CC" || event.status === "CS";
  const isRegular = event.type === "regular";
  const isOneOnOne =
    event.event_format_id === 1 || event.event_format_id === "1";
  const isInvitee = event.is_admin_invitee === "E";
  const isPending = variant === "pending";
  const hasWhen = Boolean(event.start_time && event.end_time);
  const checkboxId = `booking-${event.id}`;

  const isRescheduleRequested = event.status === "RITR";
  const isNoShow = event.status === "MN";
  const hasFlag = isRescheduleRequested || isNoShow;
  const hasFooter = hasFlag || onViewDetail || actions;

  return (
    <article
      className={clsx("se-card", {
        "is-selected": selected,
        "is-cancelled": cancelled,
        "se-card--pending": isPending,
      })}
    >
      {/* Header row: title + badges */}
      <div className="se-card__header">
        <div className="se-card__title-row">
          {showCheckbox && (
            <div className="se-card__check event-form-group">
              <div className="form-check-box">
                <input
                  type="checkbox"
                  name={checkboxId}
                  id={checkboxId}
                  onChange={(e) => onToggle && onToggle(e, event)}
                  checked={selected}
                />
                <label className="mb-0" htmlFor={checkboxId}>
                  <span></span>
                </label>
              </div>
            </div>
          )}
          <h3 className={cancelled ? "text-line-through" : undefined}>
            {event.event_name}
          </h3>
        </div>

        <div className="se-card__header-right">
          {isRegular && (
            <span className="se-card__format" translate="no">
              {isOneOnOne ? "1:1" : `1–${event.no_of_attendies}`}
            </span>
          )}
          {showRole && (
            <span className={clsx("event_label", { invite: isInvitee })}>
              {isInvitee ? "Invitee" : "Host"}
              <img src={isInvitee ? InviteeIcon : AdminIcon} alt="" />
            </span>
          )}
        </div>
      </div>

      {/* Time + countdown */}
      {hasWhen && (
        <div className={clsx("se-card__time", { "text-line-through": cancelled })}>
          <ClockIcon />
          <span>
            {event.start_time} – {event.end_time}
          </span>
          {subtitle && <em className="se-card__countdown">{subtitle}</em>}
        </div>
      )}

      {/* Info rows: attendee + venue */}
      {(event.attendee_name || event.venue) && (
        <div className="se-card__info-list">
          {event.attendee_name && (
            <div className="se-card__info-item">
              <PersonIcon />
              <span>{event.attendee_name}</span>
            </div>
          )}
          {event.venue && (
            <div className={clsx("se-card__info-item", { "text-line-through": cancelled })}>
              {getVenueIcon(event.venue)}
              <span>{event.venue}</span>
            </div>
          )}
        </div>
      )}

      {/* Cancellation note */}
      {cancelled && (
        <p className="se-card__cancelled">
          Cancelled by {event.cancellation_by}
          {event.cancellation_remarks
            ? ` · ${event.cancellation_remarks}`
            : ""}
        </p>
      )}

      {/* Extra slot (e.g. pending copy link) */}
      {extra || null}

      {/* Footer: status flag on left, action button(s) on right */}
      {hasFooter && (
        <div className="se-card__footer">
          <div className="se-card__footer-left">
            {isRescheduleRequested && (
              <span className="se-card__flag">Reschedule requested</span>
            )}
            {isNoShow && (
              <span className="se-card__flag se-card__flag--noshow">No-show</span>
            )}
          </div>
          <div className="se-card__footer-right">
            {onViewDetail && (
              <button
                type="button"
                className={clsx("view-detail-btn", {
                  "view-detail-btn--cta": isRescheduleRequested,
                })}
                onClick={(e) => {
                  e.preventDefault();
                  onViewDetail(event);
                }}
              >
                {isRescheduleRequested ? "Review request" : "View details"}
              </button>
            )}
            {actions || null}
          </div>
        </div>
      )}
    </article>
  );
};

export default ScheduledEventCard;
